/* Trial-only cart experience: same-flavour 8-day upgrade in the native recommendation slider. */
(function (root) {
  'use strict';

  function toId(value) {
    var number = parseInt(String(value == null ? '' : value).replace(/[^0-9]/g, ''), 10);
    return isNaN(number) || number <= 0 ? 0 : number;
  }

  function linePlanId(item) {
    var allocation = item && item.selling_plan_allocation;
    return toId(allocation && allocation.selling_plan && allocation.selling_plan.id || allocation && allocation.selling_plan_id);
  }

  function rules(raw) {
    return (raw && raw.rules || []).map(function (rule) {
      return { key: String(rule.key || ''), from: toId(rule.from), to: toId(rule.to), planFrom: toId(rule.planFrom), planTo: toId(rule.planTo) };
    }).filter(function (rule) { return rule.from && rule.to; });
  }

  function trialLines(cart, raw) {
    var ids = [];
    rules(raw).forEach(function (rule) { ids.push(rule.from, rule.to); });
    return (cart && cart.items || []).filter(function (item) {
      return ids.indexOf(toId(item.variant_id)) !== -1 && item.quantity > 0 && item.final_line_price > 0;
    });
  }

  function shippingState(cart, raw) {
    if (!raw || raw.shippingMeterEnabled !== true) return null;
    var lines = trialLines(cart, raw);
    if (!lines.length) return null;
    var threshold = toId(raw && raw.freeShippingThreshold) || 440000;
    var fee = toId(raw && raw.shippingFee) || 50000;
    var total = Math.max(0, Number(cart && cart.total_price) || 0);
    return {
      threshold: threshold,
      fee: fee,
      total: total,
      remaining: Math.max(0, threshold - total),
      progress: Math.max(0, Math.min(100, Math.round(total / threshold * 100)))
    };
  }

  // Shopify owns discount allocation. Compare catalogue/plan economics separately
  // from the server-calculated payable totals; never predict a coupon percentage.
  function baseUnitPrice(item) {
    var allocation = item && item.selling_plan_allocation;
    return Number(allocation && allocation.price != null ? allocation.price : item && item.original_price != null ? item.original_price : item && item.final_price);
  }

  function discountSignature(cart) {
    var codes = (cart && cart.discount_codes || []).map(function (code) {
      return [String(code.code || '').toLowerCase(), code.applicable !== false];
    }).sort();
    var applications = (cart && cart.cart_level_discount_applications || []).slice();
    (cart && cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        if (allocation.discount_application) applications.push(allocation.discount_application);
      });
    });
    var identities = applications.map(function (application) {
      return JSON.stringify([application.title || '', application.type || '', application.target_type || '',
        application.allocation_method || '', application.value_type || '', String(application.value == null ? '' : application.value)]);
    }).filter(function (identity, index, all) { return all.indexOf(identity) === index; }).sort();
    return JSON.stringify([codes, identities]);
  }

  function unrelatedSignature(cart, pending) {
    return JSON.stringify((cart && cart.items || []).filter(function (item) {
      // Lifecycle-owned zero-price gifts may change while the same marked
      // trial moves from 8 to 16 days. Preserve all unrelated lines strictly.
      var properties = item.properties || {};
      var request = pending.sourceProperties && pending.sourceProperties._trial_gift_request;
      var config = root && root.AL_TRIAL_GIFTS_B_CART;
      var gifts = config && config.gifts || {};
      var giftIds = [].concat(gifts['8'] || [], gifts['16'] || []).map(function (gift) { return toId(gift.variantId); });
      var ownGift = request && properties._trial_gift_request === request && properties._no_gifts === 'true' &&
        [pending.variantId, pending.targetVariantId].indexOf(toId(properties._trial_gift_for)) !== -1 &&
        giftIds.indexOf(toId(item.variant_id)) !== -1 && Number(item.final_line_price) === 0 && linePlanId(item) === 0;
      if (ownGift) return false;
      return !(toId(item.variant_id) === pending.variantId && linePlanId(item) === pending.sellingPlanId) &&
        !(toId(item.variant_id) === pending.targetVariantId && linePlanId(item) === pending.targetSellingPlanId);
    }).map(function (item) {
      return [toId(item.variant_id), linePlanId(item), Number(item.quantity), baseUnitPrice(item), propertySignature(item.properties)];
    }).sort(function (a, b) { return JSON.stringify(a).localeCompare(JSON.stringify(b)); }));
  }

  function hasCartDiscount(cart) {
    return Number(cart && cart.total_discount || 0) > 0 || (cart && cart.discount_codes || []).some(function (code) { return code.applicable !== false; }) ||
      (cart && cart.items || []).some(function (item) { return Number(item.total_discount || 0) > 0 || Number(item.original_line_price) > Number(item.final_line_price); });
  }

  function upgradeOffers(cart, raw) {
    if (!raw || raw.enabled !== true) return [];
    return rules(raw).reduce(function (offers, rule) {
      var cartItems = cart && cart.items || [];
      var subscriptionSources = cartItems.filter(function (item) {
        var plan = linePlanId(item);
        return toId(item.variant_id) === rule.from && item.quantity > 0 && item.final_line_price > 0 && plan > 0 && rule.planTo && (!rule.planFrom || rule.planFrom === plan);
      });
      var oneTimeSources = raw.mergeOneTime === true ? cartItems.filter(function (item) {
        return toId(item.variant_id) === rule.from && item.quantity > 0 && item.final_line_price > 0 && linePlanId(item) === 0;
      }) : [];
      var sources = subscriptionSources.length ? subscriptionSources : oneTimeSources;
      if (sources.length !== 1) return offers;
      var sourcePlan = linePlanId(sources[0]);
      var targetPlan = sourcePlan > 0 ? rule.planTo : 0;
      var hasTarget = (cart.items || []).some(function (item) {
        return toId(item.variant_id) === rule.to && item.quantity > 0 && item.final_line_price > 0 && linePlanId(item) === targetPlan;
      });
      if (hasTarget) return offers;
      var sourceQuantity = sources.reduce(function (sum, item) { return sum + item.quantity; }, 0);
      var source = sources[0];
      if (sourceQuantity % 2 !== 1) source = null;
      if (source) {
        var price = Math.max(0, baseUnitPrice(source) || 0);
        var discounted = hasCartDiscount(cart);
        var shipping = shippingState(cart, raw);
        offers.push({
          key: rule.key,
          variantId: rule.from,
          sellingPlanId: sourcePlan,
          targetVariantId: rule.to,
          targetSellingPlanId: targetPlan,
          sourceQuantity: sourceQuantity,
          image: source.image || '',
          title: source.product_title || source.title || '',
          price: price,
          discounted: discounted,
          properties: source.properties || {},
          unlocksFreeShipping: Boolean(!discounted && shipping && shipping.remaining > 0 && shipping.remaining <= price)
        });
      }
      return offers;
    }, []);
  }

  function itemDrawerTokens(item) {
    var tokens = (item && item.options_with_values || []).map(function (option) {
      return String(option && option.value || '').trim();
    }).filter(Boolean);
    var allocation = item && item.selling_plan_allocation;
    var planName = String(allocation && allocation.selling_plan && allocation.selling_plan.name || '').trim();
    tokens.push(planName || '単発購入');
    return tokens;
  }

  var ONE_TIME_16_DAY_PRICE = 550000;
  var api = { toId: toId, linePlanId: linePlanId, rules: rules, trialLines: trialLines, shippingState: shippingState, upgradeOffers: upgradeOffers, itemDrawerTokens: itemDrawerTokens, baseUnitPrice: baseUnitPrice, discountSignature: discountSignature, unrelatedSignature: unrelatedSignature, upgradeState: upgradeState, syncTrialSlide: syncTrialSlide, validUpgradeDelta: validUpgradeDelta };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root || !root.document || typeof root.fetch !== 'function') return;
  if (!root.AL_TRIAL_CART_MERGE || root.AL_TRIAL_CART_MERGE.enabled !== true || !rules(root.AL_TRIAL_CART_MERGE).length) return;

  var renderGeneration = 0;
  var renderTimer = 0;
  var renderInFlight = false;
  var renderQueued = false;
  var pendingUpgrade = null;
  var rollbackCheckoutUnlockUntil = 0;
  var lastError = '';
  var cartHostObserver = null;
  var lastOfferExpected = false;
  var lastRenderedOffers = [];
  var lastShippingFeeText = '';
  var lastShippingDrawerSignature = '';


  function yen(minor) { return '¥' + Math.round(minor / 100).toLocaleString('ja-JP'); }

  function ensureStyles() {
    if (document.getElementById('al-trial-cart-offer-style')) return;
    var style = document.createElement('style');
    style.id = 'al-trial-cart-offer-style';
    style.textContent = 'html.al-trial-cart-has-trial #g-cart-content .g-cart-tiers,html.al-trial-cart-has-trial #g-cart-content .g-cart-freeshipping{display:none!important}.al-trial-cart-error{margin:8px 16px 12px;padding:10px 12px;border-radius:6px;background:#fff0f0;color:#8b2020;font-size:13px;font-weight:700;line-height:1.45}.g-upsells-wrap.al-trial-cart-has-promo-nav .g-cab-title[hidden]{display:none!important}.g-upsells-promo.al-trial-cart-promo-with-nav{display:flex;align-items:center;justify-content:space-between;gap:12px}.al-trial-cart-promo-with-nav .g-slider-nav{display:flex;flex:0 0 auto;gap:4px}.al-trial-cart-promo-with-nav .g-cus-prev,.al-trial-cart-promo-with-nav .g-cus-next{display:grid;width:44px;height:44px;place-items:center;color:#fff!important;cursor:pointer}.al-trial-cart-promo-with-nav .g-cus-prev:focus-visible,.al-trial-cart-promo-with-nav .g-cus-next:focus-visible{outline:2px solid #fff;outline-offset:2px}.al-trial-cart-promo-with-nav .g-cus-prev svg,.al-trial-cart-promo-with-nav .g-cus-next svg{width:24px;height:24px;stroke:currentColor}.al-trial-cart-upgrade .g-cart-upsell{align-items:center}.al-trial-cart-upgrade .g-cart-upsell-content{display:grid;grid-template-columns:minmax(0,1fr);gap:6px;min-width:0;justify-content:stretch}.al-trial-cart-upgrade .g-cart-upsell-content h4{margin-bottom:0;min-width:0}.al-trial-cart-upgrade .g-cart-upsell-content h4 span{max-width:none;font-size:13px;line-height:1.2}.al-trial-cart-upgrade .g-cart-upsell-cta{align-items:center;flex-direction:row;gap:8px}.al-trial-cart-upgrade .g-cart-upsell-cta button{min-height:44px;padding:8px 10px;font-size:13px;line-height:1.25;white-space:nowrap}.al-trial-cart-upgrade .g-cart-upsell-pricing{flex:0 0 auto}.al-trial-cart-upgrade button:disabled{opacity:.65}@media(max-width:390px){.al-trial-cart-upgrade .g-cart-upsell{align-items:flex-start;gap:8px}.al-trial-cart-upgrade .g-cart-upsell-image{width:64px;height:64px;min-width:64px}.al-trial-cart-upgrade .g-cart-upsell-cta{align-items:stretch;flex-direction:column;gap:4px}.al-trial-cart-upgrade .g-cart-upsell-cta button{width:100%}html.al-trial-cart-has-trial #g-cart-content .swiper-slide:not([data-al-trial-upsell]) .g-cart-upsell{gap:8px}html.al-trial-cart-has-trial #g-cart-content .swiper-slide:not([data-al-trial-upsell]) .g-cart-upsell-image{width:64px;height:64px;min-width:64px}html.al-trial-cart-has-trial #g-cart-content .swiper-slide:not([data-al-trial-upsell]) .g-cart-upsell-content{min-width:0;gap:6px}html.al-trial-cart-has-trial #g-cart-content .swiper-slide:not([data-al-trial-upsell]) .g-cart-upsell h4 span{max-width:none;font-size:13px;line-height:1.15}html.al-trial-cart-has-trial #g-cart-content .swiper-slide:not([data-al-trial-upsell]) .g-cart-upsell-cta button{padding-left:6px;padding-right:6px;font-size:12px}}';
    style.textContent += 'html.al-trial-cart-has-trial #g-cart-content .g-upsells-promo.al-trial-cart-promo-with-nav{padding:9px 14px;margin-bottom:6px;font-size:13px;line-height:1.25;text-transform:none}html.al-trial-cart-has-trial #g-cart-content .g-cart-upsells{padding:7px 20px 9px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell{gap:10px;padding:5px;min-height:0}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-image{width:76px;height:76px;min-width:76px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-content{display:flex;align-items:center;justify-content:space-between;gap:8px;min-width:0}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-content h4{flex:1;min-width:0}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-content h4 span{max-width:none;font-size:12px;line-height:1.15}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-cta{flex:0 0 auto;align-items:center;flex-direction:column;gap:4px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-cta button{position:relative;box-sizing:border-box;width:auto;max-width:none;height:32px;min-height:32px;padding:4px 8px;font-size:12px;line-height:1;white-space:nowrap}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-cta button:after{content:"";position:absolute;inset:-6px -4px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-shipping-fee{display:inline-block;margin-top:5px;padding:3px 8px;border-radius:999px;background:#fff2f0;color:#8b3936;font-size:12px;font-weight:700;line-height:1.3}@media(max-width:390px){html.al-trial-cart-has-trial #g-cart-content .g-cart-upsells{padding:6px 14px 8px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-image{width:64px;height:64px;min-width:64px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-content{display:flex;align-items:center;gap:6px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-content h4 span{font-size:11px;line-height:1.12}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-cta{align-items:center;flex-direction:column;gap:3px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade .g-cart-upsell-cta button{width:auto;max-width:none;height:32px;min-height:32px;padding:4px 7px;font-size:11px;line-height:1}}';
    // Keep the longer pre-discount price from squeezing the mobile title.
    style.textContent += '@media(max-width:390px){html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell{align-items:flex-start}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-content{flex-direction:column;align-items:stretch;gap:8px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-content h4{flex:0 0 auto;width:100%}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-cta{align-items:stretch;gap:8px}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-pricing{white-space:normal;overflow-wrap:anywhere}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-cta button{width:100%;height:auto;min-height:44px;white-space:normal;line-height:1.25}html.al-trial-cart-has-trial #g-cart-content .al-trial-cart-upgrade.al-trial-cart-discounted .g-cart-upsell-cta button:after{inset:0}}';
    document.head.appendChild(style);
  }

  function restoreNativeUpsells() {
    document.querySelectorAll('#g-cart-content .g-upsells-wrap').forEach(function (wrap) {
      if (wrap.dataset.alTrialSuppressed !== 'true') return;
      var previous = wrap.dataset.alTrialPreviousDisplay || '';
      wrap.style.removeProperty('display');
      if (previous) wrap.style.display = previous;
      delete wrap.dataset.alTrialPreviousDisplay;
      delete wrap.dataset.alTrialSuppressed;
    });
  }

  function errorHost() {
    var items = document.querySelector('#g-cart-content #g-cart-items');
    if (!items) return null;
    var existing = document.getElementById('al-trial-cart-error');
    if (existing) return existing;
    var host = document.createElement('p');
    host.id = 'al-trial-cart-error';
    host.className = 'al-trial-cart-error';
    host.setAttribute('role', 'alert');
    items.insertAdjacentElement('afterend', host);
    return host;
  }

  function renderError() {
    var existing = document.getElementById('al-trial-cart-error');
    if (!lastError) {
      if (existing) existing.remove();
      return;
    }
    var host = existing || errorHost();
    if (host) host.textContent = lastError;
  }

  function blockCheckout(blocked) {
    document.querySelectorAll('#g-cart-content .g-cart-checkout').forEach(function (button) {
      if (blocked) {
        if (!button.dataset.alTrialPreviousDisabled) button.dataset.alTrialPreviousDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.alTrialPreviousDisabled) {
        button.disabled = button.dataset.alTrialPreviousDisabled === 'true';
        if (!button.disabled) button.removeAttribute('aria-disabled');
        delete button.dataset.alTrialPreviousDisabled;
      }
    });
  }

  function drawerContainsTokens(tokens) {
    if (!tokens || !tokens.length) return false;
    return Array.prototype.some.call(document.querySelectorAll('#g-cart-content .g-cart-item-options'), function (options) {
      var text = String(options.textContent || '').replace(/\s+/g, ' ');
      return tokens.every(function (token) { return text.indexOf(token) !== -1; });
    });
  }

  function drawerReflectsUpgrade(pending) {
    return Boolean(pending && pending.targetDrawerTokens && pending.targetDrawerTokens.length &&
      drawerContainsTokens(pending.targetDrawerTokens) &&
      !drawerContainsTokens(pending.sourceDrawerTokens));
  }

  function updateSwiper(slider) {
    if (slider && slider.swiper && typeof slider.swiper.update === 'function') slider.swiper.update();
  }

  function removeTrialSlides() {
    var sliders = [];
    document.querySelectorAll('#g-cart-content [data-al-trial-upsell]').forEach(function (slide) {
      var slider = slide.closest('.g-cart-upsells-slider');
      if (slider && sliders.indexOf(slider) === -1) sliders.push(slider);
      slide.remove();
    });
    sliders.forEach(updateSwiper);
  }

  function syncNativeShipping(hasTrial) {
    document.documentElement.classList.toggle('al-trial-cart-has-trial', Boolean(hasTrial));
    document.querySelectorAll('#g-cart-content .g-cart-tiers, #g-cart-content .g-cart-freeshipping').forEach(function (element) {
      if (hasTrial) {
        if (element.dataset.alTrialShippingSuppressed !== 'true') {
          element.dataset.alTrialPreviousDisplay = element.style.display || '';
          element.dataset.alTrialShippingSuppressed = 'true';
        }
        element.style.setProperty('display', 'none', 'important');
      } else if (element.dataset.alTrialShippingSuppressed === 'true') {
        var previous = element.dataset.alTrialPreviousDisplay || '';
        element.style.removeProperty('display');
        if (previous) element.style.display = previous;
        delete element.dataset.alTrialPreviousDisplay;
        delete element.dataset.alTrialShippingSuppressed;
      }
    });
    var custom = document.getElementById('al-trial-cart-shipping-status');
    if (custom) custom.remove();
  }

  function syncTrialShippingFeeText(labelText) {
    var activeLabels = [];
    document.querySelectorAll('#g-cart-content #g-cart-items .g-cart-item').forEach(function (item) {
      var options = item.querySelector('.g-cart-item-options');
      if (!labelText || !options || options.textContent.indexOf('8日間お試し') === -1) return;
      var content = item.querySelector('.g-ci-content');
      if (!content) return;
      var label = item.querySelector('.al-trial-cart-shipping-fee');
      if (!label) {
        label = document.createElement('div');
        label.className = 'al-trial-cart-shipping-fee';
        var variant = item.querySelector('.g-ci-variant-title');
        if (variant) variant.insertAdjacentElement('afterend', label);
        else content.appendChild(label);
      }
      if (label.textContent !== labelText) label.textContent = labelText;
      activeLabels.push(label);
    });
    document.querySelectorAll('#g-cart-content .al-trial-cart-shipping-fee').forEach(function (label) {
      if (activeLabels.indexOf(label) === -1) label.remove();
    });
  }

  function shippingDrawerSignature() {
    var items = document.querySelector('#g-cart-content #g-cart-items');
    return items ? items.textContent.replace(/送料\s*¥[\d,]+/g, '').replace(/\s+/g, ' ').trim() : '';
  }

  function restoreCachedTrialShipping() {
    var signature = shippingDrawerSignature();
    if (!signature || signature !== lastShippingDrawerSignature) {
      syncTrialShippingFeeText('');
      return;
    }
    syncNativeShipping(true);
    syncTrialShippingFeeText(lastShippingFeeText);
  }

  function syncTrialShippingFee(cart, raw) {
    var shipping = shippingState(cart, raw);
    lastShippingFeeText = shipping && shipping.remaining > 0 ? '送料 ' + yen(shipping.fee) : '';
    syncTrialShippingFeeText(lastShippingFeeText);
    lastShippingDrawerSignature = shippingDrawerSignature();
  }

  function syncTrialSlide(slide, offer) {
    slide.classList.toggle('al-trial-cart-discounted', Boolean(offer.discounted));
    slide.dataset.alTrialUpsell = offer.key;
    slide.dataset.upsellVid = String(offer.variantId);
    slide.dataset.upsellVariantIds = String(offer.variantId);
    slide.dataset.variantId = String(offer.variantId);
    slide.dataset.sellingPlanId = String(offer.sellingPlanId);
    slide.dataset.targetVariantId = String(offer.targetVariantId);
    slide.dataset.targetSellingPlanId = String(offer.targetSellingPlanId);
    slide.dataset.sourceQuantity = String(offer.sourceQuantity);
    slide.dataset.expectedDelta = String(offer.price);
    slide.dataset.sourceProperties = JSON.stringify(offer.properties || {});
    var price = slide.querySelector('.g-ci-pricing-sale');
    if (price) price.textContent = '+' + yen(offer.price) + (offer.discounted ? '（割引前）' : '');
    var button = slide.querySelector('[data-al-trial-upgrade-button]');
    if (button && !button.disabled) button.textContent = offer.unlocksFreeShipping ? '送料無料で変更' : '16日間お試しに変更';
    return slide;
  }

  function trialSlide(offer) {
    var slide = document.createElement('div');
    slide.className = 'swiper-slide al-trial-cart-upgrade';
    syncTrialSlide(slide, offer);

    var card = document.createElement('div');
    card.className = 'g-cart-upsell';
    if (offer.image) {
      var imageWrap = document.createElement('div');
      imageWrap.className = 'g-cart-upsell-image';
      var image = document.createElement('img');
      image.src = offer.image;
      image.alt = '';
      imageWrap.appendChild(image);
      card.appendChild(imageWrap);
    }

    var content = document.createElement('div');
    content.className = 'g-cart-upsell-content';
    var heading = document.createElement('h4');
    var title = document.createElement('span');
    var strong = document.createElement('strong');
    strong.textContent = '16日間お試しにアップグレード';
    title.appendChild(strong);
    var subtitle = document.createElement('span');
    subtitle.textContent = offer.key === 'variety' ? '4種セットをもう8日分追加' : '同じフレーバーをもう8日分追加';
    heading.append(title, subtitle);

    var cta = document.createElement('div');
    cta.className = 'g-cart-upsell-cta';
    var pricing = document.createElement('span');
    pricing.className = 'g-cart-upsell-pricing';
    var sale = document.createElement('span');
    sale.className = 'g-ci-pricing-sale';
    sale.textContent = '+' + yen(offer.price) + (offer.discounted ? '（割引前）' : '');
    pricing.appendChild(sale);
    var button = document.createElement('button');
    button.type = 'button';
    button.dataset.alTrialUpgradeButton = 'true';
    button.textContent = offer.unlocksFreeShipping ? '送料無料で変更' : '16日間お試しに変更';
    cta.append(pricing, button);
    content.appendChild(heading);
    content.appendChild(cta);
    card.appendChild(content);
    slide.appendChild(card);
    return slide;
  }

  function renderUpsells(offers) {
    restoreNativeUpsells();
    if (!offers.length) {
      removeTrialSlides();
      return;
    }
    var wrap = document.querySelector('#g-cart-content .g-upsells-wrap');
    var slider = wrap && wrap.querySelector('.g-cart-upsells-slider');
    var slides = slider && slider.querySelector('.swiper-wrapper');
    if (!wrap || !slider || !slides) return;
    var wanted = offers.map(function (offer) { return String(offer.key); });
    slides.querySelectorAll('[data-al-trial-upsell]').forEach(function (slide) {
      if (wanted.indexOf(String(slide.dataset.alTrialUpsell || '')) === -1) slide.remove();
    });
    offers.slice().reverse().forEach(function (offer) {
      var existing = Array.prototype.find.call(slides.querySelectorAll('[data-al-trial-upsell]'), function (slide) {
        return String(slide.dataset.alTrialUpsell || '') === String(offer.key);
      });
      var slide = existing ? syncTrialSlide(existing, offer) : trialSlide(offer);
      if (slide !== slides.firstElementChild) slides.insertBefore(slide, slides.firstElementChild);
    });
    wrap.hidden = false;
  }

  function normalizeUpsellHeader(hasTrial) {
    document.querySelectorAll('#g-cart-content .g-upsells-wrap').forEach(function (wrap) {
      var promo = wrap.querySelector('.g-upsells-promo');
      var titleRow = wrap.querySelector('.g-cab-title');
      var label = titleRow && titleRow.querySelector(':scope > span');
      var nav = wrap.querySelector('.g-slider-nav');
      var slider = wrap.querySelector('.g-cart-upsells-slider');
      if (!titleRow || !nav) return;
      if (!hasTrial) {
        if (promo && promo.dataset.alTrialOriginalText != null) {
          var restoreNode = Array.prototype.find.call(promo.childNodes, function (node) { return node.nodeType === 3; });
          if (restoreNode) restoreNode.textContent = promo.dataset.alTrialOriginalText;
          delete promo.dataset.alTrialOriginalText;
        }
        if (nav.dataset.alTrialMoved === 'true') titleRow.appendChild(nav);
        if (promo) promo.classList.remove('al-trial-cart-promo-with-nav');
        wrap.classList.remove('al-trial-cart-has-promo-nav');
        titleRow.hidden = false;
        delete nav.dataset.alTrialMoved;
        if (slider && slider.swiper) {
          if (document.documentElement.classList.contains('al-trial-cart-has-trial') && root.innerWidth <= 390) {
            if (!slider.dataset.alTrialSlidesPerView) slider.dataset.alTrialSlidesPerView = String(slider.swiper.params.slidesPerView || 1.2);
            slider.swiper.params.slidesPerView = 1.05;
          } else if (slider.dataset.alTrialSlidesPerView) {
            slider.swiper.params.slidesPerView = Number(slider.dataset.alTrialSlidesPerView);
            delete slider.dataset.alTrialSlidesPerView;
          }
          root.requestAnimationFrame(function () { updateSwiper(slider); });
        }
        return;
      }
      if (promo) {
        var freeShippingOffer = lastRenderedOffers.some(function (offer) { return offer.unlocksFreeShipping; });
        var promoText = Array.prototype.find.call(promo.childNodes, function (node) { return node.nodeType === 3; });
        var discountedOffer = lastRenderedOffers.some(function (offer) { return offer.discounted; });
        if (freeShippingOffer || discountedOffer) {
          if (!promoText) {
            promoText = document.createTextNode('');
            promo.insertBefore(promoText, promo.firstChild);
          }
          if (promo.dataset.alTrialOriginalText == null) promo.dataset.alTrialOriginalText = promoText.textContent;
          promoText.textContent = discountedOffer ? '16日間お試しに変更｜割引後の金額・送料は購入手続きで確認 ' : '16日間お試しに変更すると送料¥500が無料 ';
        } else if (promo.dataset.alTrialOriginalText != null) {
          if (promoText) promoText.textContent = promo.dataset.alTrialOriginalText;
          delete promo.dataset.alTrialOriginalText;
        }
      }
      if (promo && (!label || !label.textContent.trim())) {
        promo.appendChild(nav);
        promo.classList.add('al-trial-cart-promo-with-nav');
        wrap.classList.add('al-trial-cart-has-promo-nav');
        titleRow.hidden = true;
        nav.dataset.alTrialMoved = 'true';
      }
      var previous = nav.querySelector('.g-cus-prev');
      var next = nav.querySelector('.g-cus-next');
      [[previous, '前のおすすめ', 'prev'], [next, '次のおすすめ', 'next']].forEach(function (entry) {
        if (!entry[0]) return;
        entry[0].setAttribute('aria-label', entry[1]);
        entry[0].setAttribute('role', 'button');
        entry[0].setAttribute('tabindex', '0');
        if (entry[0].dataset.alTrialKeyboard !== 'true') {
          entry[0].dataset.alTrialKeyboard = 'true';
          entry[0].addEventListener('keydown', function (event) {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            entry[0].click();
          });
        }
      });
      if (slider && slider.swiper) {
        if (!slider.dataset.alTrialSlidesPerView) slider.dataset.alTrialSlidesPerView = String(slider.swiper.params.slidesPerView || 1.2);
        slider.swiper.params.slidesPerView = root.innerWidth <= 374 ? 1.05 : Number(slider.dataset.alTrialSlidesPerView);
        root.requestAnimationFrame(function () { updateSwiper(slider); });
      }
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) { root.setTimeout(resolve, ms); });
  }

  function cartRequest(path, body) {
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-AL-Trial-Merge': '1' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error(path + ' ' + response.status);
      return response.json();
    });
  }

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', 'X-AL-Trial-Merge': '1' } })
      .then(function (response) { if (!response.ok) throw new Error('cart read ' + response.status); return response.json(); });
  }

  function matchingLines(cart, variantId, sellingPlanId) {
    return (cart && cart.items || []).filter(function (item) {
      return toId(item.variant_id) === variantId && linePlanId(item) === sellingPlanId && item.final_line_price > 0;
    });
  }

  function matchingQuantity(cart, variantId, sellingPlanId) {
    return matchingLines(cart, variantId, sellingPlanId).reduce(function (sum, item) { return sum + item.quantity; }, 0);
  }

  function propertySignature(properties) {
    return JSON.stringify(Object.keys(sanitizedProperties(properties)).sort().map(function (key) { return [key, sanitizedProperties(properties)[key]]; }));
  }

  function sanitizedProperties(properties) {
    var clean = {};
    Object.keys(properties || {}).forEach(function (key) {
      if (key !== '_bndlr' && key !== '__') clean[key] = properties[key];
    });
    return clean;
  }

  function sameProperties(left, right) {
    return propertySignature(left) === propertySignature(right);
  }

  function validUpgradeDelta(pending) {
    return pending.expectedDelta > 0 && pending.expectedDelta === (pending.targetSellingPlanId === 0 ? ONE_TIME_16_DAY_PRICE - pending.sourceUnitPrice : pending.sourceUnitPrice);
  }

  function upgradeState(cart, pending) {
    var expectedTarget = Math.floor((pending.sourceQuantity + 1) / 2);
    var expectedSource = (pending.sourceQuantity + 1) % 2;
    var targetLines = matchingLines(cart, pending.targetVariantId, pending.targetSellingPlanId);
    var targetBaseTotal = targetLines.reduce(function (sum, item) { return sum + baseUnitPrice(item) * Number(item.quantity); }, 0);
    var structure = matchingQuantity(cart, pending.targetVariantId, pending.targetSellingPlanId) === expectedTarget &&
      matchingQuantity(cart, pending.variantId, pending.sellingPlanId) === expectedSource &&
      targetLines.every(function (item) { return sameProperties(item.properties, pending.sourceProperties); });
    var preserved = discountSignature(cart) === pending.discountSignature && unrelatedSignature(cart, pending) === pending.unrelatedSignature;
    var total = Number(cart && cart.total_price);
    var validLines = (cart && cart.items || []).every(function (item) {
      var value = Number(item.final_line_price);
      return Number.isFinite(value) && value >= 0 && value <= baseUnitPrice(item) * Number(item.quantity);
    });
    var finalLineSum = (cart && cart.items || []).reduce(function (sum, item) { return sum + Number(item.final_line_price); }, 0);
    // Cart-level discounts may be allocated in cart_level_discount_applications,
    // rather than final_line_price. The cart total is the payable authority.
    var cartDiscount = (cart && cart.cart_level_discount_applications || []).reduce(function (sum, application) { return sum + Number(application.total_allocated_amount || 0); }, 0);
    var totalsConsistent = total === finalLineSum || total === finalLineSum - cartDiscount;
    var economics = structure && preserved && validLines && targetBaseTotal === pending.expectedTargetLineTotal;
    var payable = Number.isFinite(total) && totalsConsistent && total >= 0 && total <= pending.baselineTotal + pending.expectedDelta;
    return { structure: structure, economics: economics, total: payable, complete: economics && payable };
  }

  function mergedState(cart, pending) {
    return upgradeState(cart, pending).complete;
  }

  function verifyMerged(pending) {
    var attempt = 0;
    var stableMatches = 0;
    var safeTargetObserved = false;
    var lastCart = null;
    function step() {
      return readCart().then(function (cart) {
        lastCart = cart;
        var state = upgradeState(cart, pending);
        safeTargetObserved = safeTargetObserved || (state.structure && state.economics);
        if (state.structure && state.economics) {
          var targetLine = matchingLines(cart, pending.targetVariantId, pending.targetSellingPlanId)[0];
          if (targetLine) pending.targetDrawerTokens = itemDrawerTokens(targetLine);
        }
        stableMatches = state.complete ? stableMatches + 1 : 0;
        if (stableMatches >= 2) return cart;
        attempt += 1;
        if (attempt >= 12) {
          var error = new Error('Trial upgrade could not be verified after settling');
          error.verificationUncertain = safeTargetObserved;
          error.lastCart = lastCart;
          throw error;
        }
        return wait(750).then(step);
      }).catch(function (error) {
        if (error && error.lastCart) throw error;
        attempt += 1;
        if (attempt >= 12) {
          var uncertain = new Error('Trial upgrade verification was interrupted');
          uncertain.verificationUncertain = safeTargetObserved;
          throw uncertain;
        }
        return wait(Math.min(1500, 300 + attempt * 150)).then(step);
      });
    }
    return step();
  }

  function waitForMergerIdle() {
    var merger = root.alTrialCartMerge;
    if (!merger) return Promise.resolve();
    if (typeof merger.whenIdle === 'function') return merger.whenIdle();
    return merger.running ? new Promise(function () {}) : Promise.resolve();
  }

  function removeUpgradeSources(pending, attempt) {
    attempt = attempt || 0;
    return readCart().then(function (cart) {
      var lines = matchingLines(cart, pending.variantId, pending.sellingPlanId);
      if (!lines.length) return cart;
      if (attempt >= 8) throw new Error('Trial upgrade source lines could not be removed');
      return cartRequest('/cart/change.js', { id: lines[0].key, quantity: 0 })
        .then(function () { return removeUpgradeSources(pending, attempt + 1); });
    });
  }

  function replaceUpgrade(pending) {
    var targetQuantity = Math.floor((pending.sourceQuantity + 1) / 2);
    var targetPayload = {
      id: pending.targetVariantId,
      quantity: targetQuantity,
      properties: sanitizedProperties(pending.sourceProperties)
    };
    if (pending.targetSellingPlanId) targetPayload.selling_plan = pending.targetSellingPlanId;
    return removeUpgradeSources(pending).then(function () {
      return cartRequest('/cart/add.js', targetPayload).then(function (added) {
        pending.addedTargetKey = added && added.key ? String(added.key) : '';
        pending.addedTargetQuantity = targetQuantity;
        return added;
      });
    });
  }

  function restoreOriginalCart(pending, attempt) {
    attempt = attempt || 0;
    return readCart().then(function (cart) {
      var targetLines = matchingLines(cart, pending.targetVariantId, pending.targetSellingPlanId);
      var sourceLines = matchingLines(cart, pending.variantId, pending.sellingPlanId);
      var sourceQuantity = sourceLines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
      var exactSourceQuantity = sourceLines.filter(function (item) { return sameProperties(item.properties, pending.sourceProperties); })
        .reduce(function (sum, item) { return sum + item.quantity; }, 0);
      if (!targetLines.length && sourceQuantity === pending.sourceQuantity && exactSourceQuantity === pending.sourceQuantity &&
        Number(cart && cart.total_price) === pending.baselineTotal) return cart;
      if (attempt >= 8) throw new Error('Trial upgrade rollback could not be verified');
      var ownedTarget = targetLines.filter(function (item) { return pending.addedTargetKey && String(item.key) === pending.addedTargetKey; })[0];
      if (targetLines.length && !ownedTarget) throw new Error('Trial upgrade rollback target ownership could not be verified');
      if (ownedTarget) {
        var addedQuantity = Math.max(0, Number(pending.addedTargetQuantity) || 0);
        if (!addedQuantity || ownedTarget.quantity < addedQuantity) throw new Error('Trial upgrade rollback target quantity could not be verified');
        return cartRequest('/cart/change.js', {
          id: ownedTarget.key,
          quantity: ownedTarget.quantity - addedQuantity
        }).then(function () { return restoreOriginalCart(pending, attempt + 1); });
      }
      if (sourceQuantity < pending.sourceQuantity) {
        var restorePayload = {
          id: pending.variantId,
          quantity: pending.sourceQuantity - sourceQuantity,
          properties: pending.sourceProperties
        };
        if (pending.sellingPlanId) restorePayload.selling_plan = pending.sellingPlanId;
        return cartRequest('/cart/add.js', restorePayload).then(function () { return restoreOriginalCart(pending, attempt + 1); });
      }
      // Never delete a source line we did not create. A concurrent add or app
      // property rewrite is ownership-ambiguous, so stop checkout and ask for reload.
      throw new Error('Trial upgrade rollback source ownership could not be verified');
    });
  }

  function completeUpgrade(pending) {
    if (pendingUpgrade !== pending) return;
    pending.verified = true;
    document.dispatchEvent(new Event('gOpenCart'));
    root.setTimeout(function () {
      if (pendingUpgrade === pending && pending.verified && !pending.drawerRefreshed) document.dispatchEvent(new Event('gOpenCart'));
    }, 5000);
    root.setTimeout(function () {
      if (pendingUpgrade === pending && pending.verified && !pending.drawerRefreshed) {
        lastError = 'カート内容は変更されましたが、表示を更新できませんでした。安全のため購入手続きを停止しています。ページを再読み込みしてカートをご確認ください。';
        blockCheckout(true);
        renderError();
      }
    }, 12000);
  }

  function recoverUpgrade(pending, error) {
    if (error && error.verificationUncertain) {
      pending.rollbackFailed = true;
      lastError = 'カート内容の確定に時間がかかっています。安全のため購入手続きを停止しています。ページを再読み込みしてカートをご確認ください。';
      blockCheckout(true);
      renderError();
      return Promise.resolve();
    }
    // The manual upgrade lease already suppresses background merging. Waiting
    // for queued work here would deadlock because endUpgrade() drains that queue.
    return readCart().then(function (cart) {
      if (mergedState(cart, pending)) {
        var recoveredTarget = matchingLines(cart, pending.targetVariantId, pending.targetSellingPlanId)[0];
        if (recoveredTarget) pending.targetDrawerTokens = itemDrawerTokens(recoveredTarget);
        completeUpgrade(pending);
        return true;
      }
      return restoreOriginalCart(pending).then(function () { return false; });
    }).then(function (upgradeCompleted) {
      if (upgradeCompleted) return;
      pendingUpgrade = null;
      var merger = root.alTrialCartMerge;
      if (merger && merger.halted && typeof merger.reset === 'function') merger.reset();
      rollbackCheckoutUnlockUntil = Date.now() + 5000;
      blockCheckout(false);
      lastError = '変更できませんでした。カート内容は変更されていません。';
      document.dispatchEvent(new Event('gOpenCart'));
      scheduleRender(0);
    }).catch(function (rollbackError) {
      pending.rollbackFailed = true;
      lastError = 'カートの変更を完了できませんでした。安全のため購入手続きを停止しています。ページを再読み込みしてカートをご確認ください。';
      blockCheckout(true);
      renderError();
      if ((root.AL_TRIAL_CART_MERGE || {}).debug && root.console) console.warn('[al-trial-cart-offer]', error, rollbackError);
    });
  }

  function render() {
    if (renderInFlight) {
      renderQueued = true;
      return;
    }
    renderInFlight = true;
    var generation = ++renderGeneration;
    var config = root.AL_TRIAL_CART_MERGE || {};
    readCart()
      .then(function (cart) {
        if (generation !== renderGeneration) return;
        var hasTrial = trialLines(cart, config).length > 0;
        var merger = root.alTrialCartMerge;
        var mergerReady = merger && !merger.halted && typeof merger.run === 'function';
        ensureStyles();
        syncNativeShipping(hasTrial);
        syncTrialShippingFee(cart, config);
        renderError();
        if (pendingUpgrade) {
          if (pendingUpgrade.verified && drawerReflectsUpgrade(pendingUpgrade)) pendingUpgrade.drawerRefreshed = true;
          blockCheckout(true);
          normalizeUpsellHeader(true);
          if (pendingUpgrade.verified && pendingUpgrade.drawerRefreshed) {
            pendingUpgrade = null;
            rollbackCheckoutUnlockUntil = 0;
            lastOfferExpected = false;
            blockCheckout(false);
            lastError = '';
            renderError();
            renderUpsells([]);
            normalizeUpsellHeader(false);
          }
          return;
        }
        if (merger && merger.halted) {
          var unsafeHaltedCart = typeof merger.plan === 'function' && merger.plan(cart, config).merged.length > 0;
          if (unsafeHaltedCart) {
            lastError = 'カート内容を安全に確定できないため、購入手続きを停止しています。8日間お試しの数量を1点に戻してください。';
            blockCheckout(true);
            renderError();
            renderUpsells([]);
            normalizeUpsellHeader(false);
            return;
          }
          if (typeof merger.reset === 'function') merger.reset();
          mergerReady = typeof merger.run === 'function';
        }
        if (Date.now() < rollbackCheckoutUnlockUntil) blockCheckout(false);
        var offers = mergerReady ? upgradeOffers(cart, config) : [];
        lastOfferExpected = offers.length > 0;
        lastRenderedOffers = offers.slice();
        renderUpsells(offers);
        normalizeUpsellHeader(offers.length > 0);
      })
      .catch(function (error) { if (generation === renderGeneration && config.debug && root.console) console.warn('[al-trial-cart-offer]', error); })
      .then(function () {
        renderInFlight = false;
        if (!renderQueued) return;
        renderQueued = false;
        scheduleRender(0);
      });
  }

  function scheduleRender(delay) {
    root.clearTimeout(renderTimer);
    renderTimer = root.setTimeout(render, delay || 0);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-al-trial-upgrade-button]');
    if (!button) return;
    var slide = button.closest('[data-al-trial-upsell]');
    if (!slide || pendingUpgrade) return;
    var merger = root.alTrialCartMerge;
    if (!merger || merger.halted || typeof merger.run !== 'function' || typeof merger.beginUpgrade !== 'function' || !merger.beginUpgrade()) {
      lastError = '別の画面でカートを更新中です。少し待ってから、もう一度お試しください。';
      renderError();
      return;
    }
    rollbackCheckoutUnlockUntil = 0;
    pendingUpgrade = {
      key: String(slide.dataset.alTrialUpsell || ''),
      variantId: toId(slide.dataset.variantId),
      sellingPlanId: toId(slide.dataset.sellingPlanId),
      targetVariantId: toId(slide.dataset.targetVariantId),
      targetSellingPlanId: toId(slide.dataset.targetSellingPlanId),
      sourceQuantity: Math.max(1, toId(slide.dataset.sourceQuantity)),
      expectedDelta: Math.max(0, toId(slide.dataset.expectedDelta)),
      baselineTotal: null,
      sourceUnitPrice: null,
      expectedTargetLineTotal: null,
      mutationStarted: false,
      addedTargetKey: '',
      addedTargetQuantity: 0,
      sourceProperties: (function () { try { return JSON.parse(slide.dataset.sourceProperties || '{}'); } catch (error) { return {}; } })(),
      sourceDrawerTokens: [],
      targetDrawerTokens: [],
      verified: false,
      drawerRefreshed: false,
      rollbackFailed: false
    };
    var activeUpgrade = pendingUpgrade;
    lastError = '';
    renderError();
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.textContent = '変更しています…';
    blockCheckout(true);
    readCart()
      .then(function (cart) {
        var currentSourceLines = matchingLines(cart, activeUpgrade.variantId, activeUpgrade.sellingPlanId);
        if (currentSourceLines.length !== 1 || currentSourceLines[0].quantity !== activeUpgrade.sourceQuantity ||
          matchingQuantity(cart, activeUpgrade.targetVariantId, activeUpgrade.targetSellingPlanId) !== 0) {
          throw new Error('Cart changed before trial upgrade started');
        }
        activeUpgrade.sourceProperties = currentSourceLines[0].properties || {};
        activeUpgrade.sourceDrawerTokens = itemDrawerTokens(currentSourceLines[0]);
        activeUpgrade.sourceUnitPrice = baseUnitPrice(currentSourceLines[0]);
        activeUpgrade.discountSignature = discountSignature(cart);
        activeUpgrade.unrelatedSignature = unrelatedSignature(cart, activeUpgrade);
        var targetQuantity = Math.floor((activeUpgrade.sourceQuantity + 1) / 2);
        activeUpgrade.expectedTargetLineTotal = activeUpgrade.targetSellingPlanId === 0
          ? ONE_TIME_16_DAY_PRICE * targetQuantity
          : activeUpgrade.sourceUnitPrice * (activeUpgrade.sourceQuantity + 1);
        activeUpgrade.baselineTotal = Number(cart.total_price);
        if (!Number.isFinite(activeUpgrade.baselineTotal) || !Number.isFinite(activeUpgrade.sourceUnitPrice) || activeUpgrade.sourceUnitPrice <= 0 || !validUpgradeDelta(activeUpgrade)) {
          throw new Error('Trial upgrade price could not be verified');
        }
        activeUpgrade.mutationStarted = true;
        return replaceUpgrade(activeUpgrade);
      })
      .then(function () { return verifyMerged(activeUpgrade); })
      .then(function () {
        if (typeof merger.endUpgrade === 'function') merger.endUpgrade();
        completeUpgrade(activeUpgrade);
      })
      .catch(function (error) {
        if (pendingUpgrade !== activeUpgrade) {
          if (typeof merger.endUpgrade === 'function') merger.endUpgrade();
          return;
        }
        if (activeUpgrade.mutationStarted) {
          return recoverUpgrade(activeUpgrade, error).then(function () {
            // Retain the lease when cart ownership is uncertain: checkout is
            // blocked, and background merge must not mutate it before reload.
            if (!activeUpgrade.rollbackFailed && typeof merger.endUpgrade === 'function') merger.endUpgrade();
          }, function (recoveryError) {
            activeUpgrade.rollbackFailed = true;
            throw recoveryError;
          });
        }
        pendingUpgrade = null;
        rollbackCheckoutUnlockUntil = Date.now() + 5000;
        blockCheckout(false);
        lastError = 'カート内容が変わったため、変更を開始しませんでした。内容をご確認のうえ、もう一度お試しください。';
        renderError();
        scheduleRender(0);
        if (typeof merger.endUpgrade === 'function') merger.endUpgrade();
      });
  });

  // Fail closed during the brief interval while the theme rebuilds the drawer
  // and before an asynchronous cart read can re-apply button state.
  document.addEventListener('click', function (event) {
    var checkout = event.target.closest && event.target.closest('#g-cart-content .g-cart-checkout');
    if (!checkout || !pendingUpgrade) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    blockCheckout(true);
  }, true);

  document.addEventListener('gOpenCart', function () {
    observeDrawerRefresh();
    if (document.documentElement.classList.contains('al-trial-cart-has-trial')) {
      restoreCachedTrialShipping();
    }
    scheduleRender(0);
  });
  document.addEventListener('al:trial-cart-merged', function () { scheduleRender(0); });
  function observeDrawerRefresh() {
    var host = document.querySelector('#g-cart-content');
    if (!host || host.dataset.alTrialOfferObserved === 'true') return;
    host.dataset.alTrialOfferObserved = 'true';
    new MutationObserver(function (mutations) {
      var drawer = host.querySelector('.g-cart-drawer');
      if (!drawer) return;
      if (document.documentElement.classList.contains('al-trial-cart-has-trial')) {
        restoreCachedTrialShipping();
      }
      // Native redraws may replace either the drawer or a nested items node.
      // Do not infer completion from mutation ownership alone: the visible
      // cart must contain the exact target option signature and no longer show
      // the exact source signature before checkout can be released.
      var drawerRefreshed = mutations.length > 0 && pendingUpgrade && pendingUpgrade.verified && drawerReflectsUpgrade(pendingUpgrade);
      var offerWasRemoved = !pendingUpgrade &&
        document.documentElement.classList.contains('al-trial-cart-has-trial') &&
        drawer.querySelector('.g-cart-upsells-slider') &&
        !drawer.querySelector('[data-al-trial-upsell]');
      if (!drawerRefreshed && !offerWasRemoved) return;
      if (offerWasRemoved && lastOfferExpected && lastRenderedOffers.length) {
        renderUpsells(lastRenderedOffers);
        normalizeUpsellHeader(true);
      }
      if (pendingUpgrade) blockCheckout(true);
      if (pendingUpgrade || document.documentElement.classList.contains('al-trial-cart-has-trial')) syncNativeShipping(true);
      if (drawerRefreshed && pendingUpgrade && pendingUpgrade.verified) pendingUpgrade.drawerRefreshed = true;
      scheduleRender(0);
    }).observe(host, { childList: true, subtree: true });
  }
  function observeCartHostReplacement() {
    if (cartHostObserver || !document.body) return;
    cartHostObserver = new MutationObserver(function () {
      var host = document.querySelector('#g-cart-content');
      if (!host || host.dataset.alTrialOfferObserved === 'true') return;
      observeDrawerRefresh();
      if (document.documentElement.classList.contains('al-trial-cart-has-trial')) {
        restoreCachedTrialShipping();
      }
      scheduleRender(50);
    });
    cartHostObserver.observe(document.body, { childList: true, subtree: true });
  }
  var lastViewportWidth = root.innerWidth;
  root.addEventListener('resize', function () {
    if (root.innerWidth === lastViewportWidth) return;
    lastViewportWidth = root.innerWidth;
    scheduleRender(100);
    root.setTimeout(function () { scheduleRender(0); }, 600);
    root.setTimeout(function () { observeDrawerRefresh(); scheduleRender(0); }, 1400);
  });
  root.setInterval(function () {
    var drawer = document.querySelector('#g-cart-content.g-cart-open .g-cart-drawer');
    var wrap = drawer && drawer.querySelector('.g-upsells-wrap');
    var promo = wrap && wrap.querySelector('.g-upsells-promo');
    var titleRow = wrap && wrap.querySelector('.g-cab-title');
    var nav = wrap && wrap.querySelector('.g-slider-nav');
    var trialPresentationMissing = !pendingUpgrade && lastOfferExpected && drawer && drawer.querySelector('.g-cart-upsells-slider') && (
      !drawer.querySelector('[data-al-trial-upsell]') ||
      !titleRow || !titleRow.hidden ||
      !promo || !nav || !promo.contains(nav)
    );
    if (trialPresentationMissing) scheduleRender(0);
    if (pendingUpgrade) blockCheckout(true);
    else if (Date.now() < rollbackCheckoutUnlockUntil) blockCheckout(false);
  }, 500);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { observeCartHostReplacement(); observeDrawerRefresh(); scheduleRender(0); });
  else { observeCartHostReplacement(); observeDrawerRefresh(); scheduleRender(0); }
  root.alTrialCartOffer = api;
})(typeof window !== 'undefined' ? window : null);
